import { AbstractControl, FormControl } from '@angular/forms';
import { Injectable } from '@angular/core';
import { Severity } from '../models/severity';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AppUtilityService {
  markAllAsDirty(control: AbstractControl) {
    if (Object.prototype.hasOwnProperty.call(control, 'controls')) {
      control.markAsDirty(); // mark group
      const ctrl = <any>control;
      for (const inner in ctrl.controls) {
        this.markAllAsDirty(ctrl.controls[inner] as AbstractControl);
      }
    } else {
      (<FormControl>control).markAsDirty();
    }
  }

  /**
   * replace umlaute to code
   * @param filename
   */
  deUmlaut = function (filename: string) {
    const umlautMap: { [key: string]: string } = {
      '\u00dc': 'UE',
      '\u00c4': 'AE',
      '\u00d6': 'OE',
      '\u00fc': 'ue',
      '\u00e4': 'ae',
      '\u00f6': 'oe',
      '\u00df': 'ss',
    };
    let regEx;

    for (const umlaut in umlautMap) {
      const replacement = umlautMap[umlaut];
      regEx = new RegExp(umlaut, 'g');
      filename = filename.replace(regEx, replacement);
    }

    return filename;
  };

  formatNumberToGerman(value: number, decimalPlaces = 1): string {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: decimalPlaces < 0 ? 1 : decimalPlaces,
      maximumFractionDigits: decimalPlaces < 0 ? 1 : decimalPlaces,
    }).format(value);
  }

  getSeverityIcon(severity: Severity) {
    switch (severity) {
      case 'success':
        return 'fas fa-circle-plus';

      case 'info':
        return 'fas fa-save';

      case 'warning':
        return 'fas fa-exclamation-triangle';

      case 'error':
        return 'fas fa-exclamation-circle';

      case 'lock':
        return 'fas fa-lock';

      default:
        return 'fas fa-ban';
    }
  }
}
export function sanitizeFilename(filename: string, maxLength = 255): string {
  return filename
    .replace(/[/\\?%*:|"<>.,]/g, '') // Remove invalid characters
    .trim() // Trim spaces
    .replace(/\s+/g, '_') // Replace spaces with underscores (optional)
    .substring(0, maxLength); // Ensure it doesn't exceed the max length
}

export function getUploadPath(
  subDir: string,
  filename: string,
  absolute = true
): string {
  if (absolute) return `${environment.apiUrl}/uploads/${subDir}/${filename}`;
  return `uploads/${subDir}/${filename}`;
}

/**
 * Removes all HTML tags from the given string.
 *
 * @param {string} text - The string containing HTML content to be cleaned.
 * @return {string} The input string without any HTML tags.
 */
export function removeHtmlFromString(text: string): string {
  return text.replace(/<[^>]+>/gm, '').replace(/&[a-z]+;/gi, '');
}

/**
 * Generates a unique slug by transforming the input text into a URL-friendly format and ensuring it does not duplicate any slugs in the provided list.
 *
 * @param {string} text - The input text to be transformed into a slug.
 * @param {string} allSlugs - A string containing existing slugs to avoid duplication.
 * @param {string} currentValue - A value which bypass the uniqueness because its already persisted.
 * @return {string} The generated unique slug.
 */
export function generateSlug(
  text: string,
  allSlugs: string[],
  currentValue?: string
): string {
  const slug = text
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  if (slug && slug === currentValue) return slug;
  let counter = 1;
  let uniqueSlug = slug;
  while (allSlugs.includes(uniqueSlug)) {
    uniqueSlug = `${slug}-${counter}`;
    counter++;
  }
  return uniqueSlug;
}

/**
 * Removes the trailing slash (/) from a string, if present.
 *
 * @param {string} input - The string to process.
 * @return {string} The string without a trailing slash.
 */
export function removeEndingSlash(input: string): string {
  return input.endsWith('/') ? input.slice(0, -1) : input;
}

/**
 * Checks if a string contains meaningful non-HTML content.
 *
 * @param {string | undefined | null} text - The string containing HTML content to be validated.
 * @return {boolean} True if the string contains meaningful non-HTML content, false otherwise.
 */
export function hasMeaningfulContent(text: string | undefined | null): boolean {
  if (!text) return false;

  // Remove all HTML tags and entities
  const cleaned = removeHtmlFromString(text);

  // Trim whitespace and check if anything meaningful is left
  return cleaned.trim().length > 0;
}

/**
 * Creates a debounced version of the provided function with separate handlers for mouseover and mouseout events.
 * This ensures that the function `fn` is invoked after a specified delay (`wait`) upon triggering the `mouseover` event,
 * while the `mouseout` event cancels any pending invocation.
 *
 * @param {F} fn - The function to be debounced and executed.
 * @param {number} wait - The delay in milliseconds to wait before invoking the function after the `mouseover` event.
 * @return {{ mouseover: F, mouseout: F }} An object containing debounced `mouseover` and `mouseout` handlers.
 */
export function debounceIntent<F extends (...args: any[]) => void>(
  fn: F,
  wait: number
): {
  mouseover: F;
  mouseout: F;
} {
  let timeout: any;
  let lastArgs: any[] | null = null;
  let lastThis: any = null;

  const mouseover = function (this: any, ...args: any[]) {
    lastArgs = args;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    lastThis = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      fn.apply(lastThis, lastArgs!);
      lastArgs = null;
    }, wait);
  } as F;

  const mouseout = function () {
    clearTimeout(timeout);
    lastArgs = null;
  } as F;

  return { mouseover, mouseout };
}

export function isKeySelected(
  selectedFeatures: Array<{ key: string; type: string }> | null | undefined,
  key: string,
  type?: string
): boolean {
  if (!selectedFeatures || !selectedFeatures.length) return false;

  // If type is provided, match both key and type
  if (type) {
    return selectedFeatures.some(
      item => item.key === String(key) && item.type === type
    );
  }

  // Otherwise, just check if the key exists regardless of type
  return selectedFeatures.some(item => item.key === String(key));
}

/**
 * Checks if a given expiration date has passed.
 *
 * @param {string | Date} expiresAt - The expiration date to check. It can be a string in a date format or a Date object.
 * @return {boolean} Returns true if the date has expired, otherwise false.
 */
export function isExpired(expiresAt: string | Date): boolean {
  const now = new Date();
  const expiresDate =
    typeof expiresAt === 'string' ? new Date(expiresAt) : expiresAt;

  return expiresDate.getTime() < now.getTime();
}

/**
 * Converts a Unix timestamp to milliseconds if it's in seconds format.
 *
 * @param {number} timestamp - The Unix timestamp to convert. If it's in seconds, it will be converted to milliseconds.
 * @return {number} The converted timestamp in milliseconds.
 */
export function millisecondsTimestamp(timestamp?: number): number {
  if (!timestamp) return 0;
  if (String(timestamp).length <= 10) {
    return timestamp * 1000;
  } else {
    return timestamp;
  }
}
